/**
 * An inspection in progress — what the capture page keeps in `localStorage` so a reload, a dead
 * battery or a dropped signal does not lose the inspector's answers (PLAN §6, Drafts).
 *
 * WHAT IS KEPT: the answers, the uploaded photos' URLs and the client_ref. The client_ref is the
 * whole reason a draft can be resubmitted safely: record_inspection returns the row the first submit
 * wrote (`duplicate: true`) when it sees the same one again, and the photos already sit in its
 * folder. A photo still uploading at a reload is lost (v1).
 *
 * RESUMABLE ON THE DAY IT WAS STARTED, by the server's Ghana day. An older draft is not resumed:
 * the page offers "Start again", which discards it.
 *
 * EVERY STORAGE ACCESS IS WRAPPED. Private windows, blocked site data and full quotas all throw;
 * the page must still work without a draft, so a failed read is "no draft" and a failed write is
 * reported, never thrown. Storage is injected so the rules here run under vitest's node
 * environment.
 */

import type {
    ChecklistLine,
    Checklist,
    Grade,
    InspectionKind,
    OpenItem,
    PhotoSlot,
    SubmittedLine,
    SubmittedPhoto,
    Visit,
} from '@/lib/inspections';
import { SLOT_ORDER } from '@/lib/inspections';

export interface DraftLine {
    /** The grade chosen. NULL: unanswered. A Must fix with `fixedOnSpot` is sent as Fixed. */
    grade: Grade | null;
    note: string;
    /** "Fixed during inspection": a Must fix put right in this visit, sent as Fixed with its note. */
    fixedOnSpot: boolean;
    /** Public-style URLs of this line's uploaded photos. */
    photos: string[];
}

export interface InspectionDraft {
    version: 1;
    vehicleId: number;
    kind: InspectionKind;
    /** Lower-case uuid: it names the photo folder and makes a retried submit safe. */
    clientRef: string;
    /** YYYY-MM-DD, the server's Ghana day when the draft began. */
    startedOn: string;
    odometerKm: string;
    lastServiceKm: string;
    remarks: string;
    lines: Record<string, DraftLine>;
    /** Re-check only: lines added beyond the open items, in the order they were added. */
    extraItems: string[];
    slots: Partial<Record<PhotoSlot, string>>;
    /** The next n for an `<item>-<n>.jpg` photo name. Never reused, so a retake is a new object. */
    nextPhoto: number;
}

export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> &
    Partial<Pick<Storage, 'key' | 'length'>>;

const PREFIX = 'fleet-admin:inspection-draft:v1:';
const GRADES: readonly Grade[] = ['ok', 'fix_soon', 'fix_now', 'fixed', 'not_needed'];
const YMD = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function draftKey(vehicleId: number, kind: InspectionKind): string {
    return `${PREFIX}${vehicleId}:${kind}`;
}

/** The page's storage, or null when the browser refuses it (private mode, blocked site data). */
export function browserStorage(): StorageLike | null {
    try {
        if (typeof window === 'undefined') return null;
        const storage = window.localStorage;
        const probe = `${PREFIX}probe`;
        storage.setItem(probe, '1');
        storage.removeItem(probe);
        return storage;
    } catch {
        return null;
    }
}

type CryptoLike = { getRandomValues<T extends ArrayBufferView | null>(array: T): T; randomUUID?: () => string };

/** A fresh lower-case v4 uuid. randomUUID needs a secure context, so getRandomValues backs it. */
export function newClientRef(cryptoImpl: CryptoLike = globalThis.crypto): string {
    if (typeof cryptoImpl.randomUUID === 'function') return cryptoImpl.randomUUID().toLowerCase();
    const b = new Uint8Array(16);
    cryptoImpl.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function emptyLine(): DraftLine {
    return { grade: null, note: '', fixedOnSpot: false, photos: [] };
}

/** A pinned open item's line as a new draft starts it (PLAN §6 step 5, and Re-check). */
function pinnedLine(kind: InspectionKind, o: OpenItem): DraftLine {
    return { ...emptyLine(), grade: kind === 'recheck' && o.grade === 'fix_soon' ? 'fix_soon' : null, note: o.note ?? '' };
}

/**
 * A new draft. Open items are pinned with their carried note pre-filled (PLAN §6 step 5); on a
 * re-check an open Fix soon starts on "Still fix soon", so a release costs one tap per Must fix.
 */
export function createDraft(args: {
    vehicleId: number;
    kind: InspectionKind;
    today: string;
    clientRef: string;
    openItems: readonly OpenItem[];
}): InspectionDraft {
    const lines: Record<string, DraftLine> = {};
    for (const o of args.openItems) lines[o.item] = pinnedLine(args.kind, o);
    return {
        version: 1,
        vehicleId: args.vehicleId,
        kind: args.kind,
        clientRef: args.clientRef.toLowerCase(),
        startedOn: args.today,
        odometerKm: '',
        lastServiceKm: '',
        remarks: '',
        lines,
        extraItems: [],
        slots: {},
        nextPhoto: 1,
    };
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function normaliseLine(value: unknown): DraftLine | null {
    if (!value || typeof value !== 'object') return null;
    const v = value as Record<string, unknown>;
    const grade = v.grade === null || GRADES.includes(v.grade as Grade) ? (v.grade as Grade | null) : undefined;
    if (grade === undefined || typeof v.note !== 'string' || typeof v.fixedOnSpot !== 'boolean' || !isStringArray(v.photos)) {
        return null;
    }
    return { grade, note: v.note, fixedOnSpot: v.fixedOnSpot, photos: v.photos };
}

/** A stored value as a draft, or null when it is not one this version wrote. */
export function parseDraft(raw: string | null | undefined): InspectionDraft | null {
    if (!raw) return null;
    let value: unknown;
    try {
        value = JSON.parse(raw);
    } catch {
        return null;
    }
    if (!value || typeof value !== 'object') return null;
    const d = value as Record<string, unknown>;
    if (
        d.version !== 1 ||
        typeof d.vehicleId !== 'number' ||
        (d.kind !== 'monthly' && d.kind !== 'recheck') ||
        typeof d.clientRef !== 'string' ||
        !UUID.test(d.clientRef) ||
        typeof d.startedOn !== 'string' ||
        !YMD.test(d.startedOn) ||
        typeof d.odometerKm !== 'string' ||
        typeof d.lastServiceKm !== 'string' ||
        typeof d.remarks !== 'string' ||
        !d.lines ||
        typeof d.lines !== 'object' ||
        !isStringArray(d.extraItems) ||
        !d.slots ||
        typeof d.slots !== 'object' ||
        typeof d.nextPhoto !== 'number'
    ) {
        return null;
    }
    const lines: Record<string, DraftLine> = {};
    for (const [item, line] of Object.entries(d.lines as Record<string, unknown>)) {
        const parsed = normaliseLine(line);
        if (!parsed) return null;
        lines[item] = parsed;
    }
    const slots: Partial<Record<PhotoSlot, string>> = {};
    for (const slot of SLOT_ORDER) {
        const url = (d.slots as Record<string, unknown>)[slot];
        if (typeof url === 'string') slots[slot] = url;
    }
    return {
        version: 1,
        vehicleId: d.vehicleId,
        kind: d.kind,
        clientRef: d.clientRef,
        startedOn: d.startedOn,
        odometerKm: d.odometerKm,
        lastServiceKm: d.lastServiceKm,
        remarks: d.remarks,
        lines,
        extraItems: d.extraItems,
        slots,
        nextPhoto: d.nextPhoto,
    };
}

export function readDraft(storage: StorageLike | null, vehicleId: number, kind: InspectionKind): InspectionDraft | null {
    if (!storage) return null;
    try {
        const draft = parseDraft(storage.getItem(draftKey(vehicleId, kind)));
        return draft && draft.vehicleId === vehicleId && draft.kind === kind ? draft : null;
    } catch {
        return null;
    }
}

/** True when the draft was stored. A false is shown to the inspector, never thrown. */
export function writeDraft(storage: StorageLike | null, draft: InspectionDraft): boolean {
    if (!storage) return false;
    try {
        storage.setItem(draftKey(draft.vehicleId, draft.kind), JSON.stringify(draft));
        return true;
    } catch {
        return false;
    }
}

export function deleteDraft(storage: StorageLike | null, vehicleId: number, kind: InspectionKind): void {
    if (!storage) return;
    try {
        storage.removeItem(draftKey(vehicleId, kind));
    } catch {
        // Nothing to do: a draft that cannot be removed is simply resumed or discarded later.
    }
}

/** A draft resumes on the Ghana day it was started; after that it can only be started again. */
export function isResumable(draft: Pick<InspectionDraft, 'startedOn'>, today: string): boolean {
    return draft.startedOn === today;
}

export interface DraftSummary {
    vehicleId: number;
    kind: InspectionKind;
    startedOn: string;
}

/** Every draft in storage, for the board's "Resume" markers. */
export function listDrafts(storage: StorageLike | null): DraftSummary[] {
    const out: DraftSummary[] = [];
    if (!storage || typeof storage.key !== 'function' || typeof storage.length !== 'number') return out;
    try {
        for (let i = 0; i < storage.length; i += 1) {
            const key = storage.key(i);
            if (!key || !key.startsWith(PREFIX)) continue;
            const draft = parseDraft(storage.getItem(key));
            if (draft) out.push({ vehicleId: draft.vehicleId, kind: draft.kind, startedOn: draft.startedOn });
        }
    } catch {
        return out;
    }
    return out;
}

// ─── The lines a visit grades ───────────────────────────────────────────────

/** The checklist lines in force on `today` (added_on on or before it), in checklist order. */
export function linesInForce(checklist: Checklist, today: string): ChecklistLine[] {
    return checklist.lines.filter((l) => l.added_on <= today).sort((a, b) => a.position - b.position);
}

/**
 * The items this visit grades, in the order they are sent: a monthly grades every line in force; a
 * re-check grades the car's open items (the server's order: Must fix first) and any lines added.
 */
export function draftItems(
    kind: InspectionKind,
    inForce: readonly ChecklistLine[],
    openItems: readonly OpenItem[],
    extraItems: readonly string[],
): string[] {
    if (kind === 'monthly') return inForce.map((l) => l.key);
    const open = openItems.map((o) => o.item);
    return [...open, ...extraItems.filter((i) => !open.includes(i))];
}

/** The grade a line is sent with: a Must fix fixed during the inspection goes as Fixed. */
export function effectiveGrade(line: DraftLine | undefined): Grade | null {
    if (!line || !line.grade) return null;
    return line.grade === 'fix_now' && line.fixedOnSpot ? 'fixed' : line.grade;
}

/** Fix soon, Must fix (fixed on the spot included) and No fix needed carry a note. */
export function noteRequired(line: DraftLine | undefined): boolean {
    return line?.grade === 'fix_soon' || line?.grade === 'fix_now' || line?.grade === 'not_needed';
}

/**
 * The open items whose run already carries a Must-fix photo that record_inspection will count
 * (decision 3): a photo on an earlier Must-fix line of the run on the same contract, or on any
 * contract when the visit has none. A Fix soon line's photo does not count — a Fix soon raised to
 * Must fix is a new Must fix. Mirrors the server only so Submit is not offered for a call it
 * would refuse; the server decides.
 */
export function carriedPhotoItems(
    openItems: readonly OpenItem[],
    visits: readonly Visit[],
    contractId: number | null,
): Set<string> {
    const lineInfo = new Map<number, { grade: Grade; contractId: number | null }>();
    for (const v of visits) for (const l of v.lines) lineInfo.set(l.id, { grade: l.grade, contractId: v.contract_id });
    const out = new Set<string>();
    for (const o of openItems) {
        const counted = o.photos.some((p) => {
            const info = lineInfo.get(p.line_id);
            return !!info && info.grade === 'fix_now' && (contractId === null || info.contractId === contractId);
        });
        if (counted) out.add(o.item);
    }
    return out;
}

/** A new Must fix needs its own photo while "Fixed during inspection" is off. */
export function photoRequired(line: DraftLine | undefined, item: string, carried: ReadonlySet<string>): boolean {
    return line?.grade === 'fix_now' && !line.fixedOnSpot && !carried.has(item);
}

export type DraftProblem =
    | { item: string; problem: 'unanswered' | 'note' | 'photo' }
    | { item: null; problem: 'empty' };

/** What still stops Submit, line by line, in the order the lines are shown. */
export function draftProblems(draft: InspectionDraft, items: readonly string[], carried: ReadonlySet<string>): DraftProblem[] {
    if (items.length === 0) return [{ item: null, problem: 'empty' }];
    const out: DraftProblem[] = [];
    for (const item of items) {
        const line = draft.lines[item];
        if (!line?.grade) {
            out.push({ item, problem: 'unanswered' });
            continue;
        }
        if (noteRequired(line) && !line.note.trim()) out.push({ item, problem: 'note' });
        if (photoRequired(line, item, carried) && line.photos.length === 0) out.push({ item, problem: 'photo' });
    }
    return out;
}

/**
 * The record_inspection arguments for the lines and photos. Unanswered lines are left out (Submit
 * is not offered while any remain). An OK line carries no note and no photo: its note box is
 * hidden, and the server refuses a photo on an OK line. Slot photos go with a monthly only.
 */
export function buildSubmission(draft: InspectionDraft, items: readonly string[]): { lines: SubmittedLine[]; photos: SubmittedPhoto[] } {
    const lines: SubmittedLine[] = [];
    const photos: SubmittedPhoto[] = [];
    if (draft.kind === 'monthly') {
        for (const slot of SLOT_ORDER) {
            const url = draft.slots[slot];
            if (url) photos.push({ url, slot });
        }
    }
    for (const item of items) {
        const line = draft.lines[item];
        const grade = effectiveGrade(line);
        if (!line || !grade) continue;
        lines.push({ item, grade, note: grade === 'ok' ? null : line.note.trim() || null });
        if (grade !== 'ok') for (const url of line.photos) photos.push({ url, item });
    }
    return { lines, photos };
}

// ─── Edits ──────────────────────────────────────────────────────────────────

export function setLine(draft: InspectionDraft, item: string, patch: Partial<DraftLine>): InspectionDraft {
    const current = draft.lines[item] ?? emptyLine();
    return { ...draft, lines: { ...draft.lines, [item]: { ...current, ...patch } } };
}

/** The grades a row offers: a pinned open item closes or stays open, never OK; any other line is OK, Fix soon or Must fix. */
const PINNED_GRADES: ReadonlySet<Grade> = new Set(['fixed', 'not_needed', 'fix_now', 'fix_soon']);
const NEW_GRADES: ReadonlySet<Grade> = new Set(['ok', 'fix_soon', 'fix_now']);

/**
 * A stored draft brought up to date with the car's open items as just read — on resume, and after
 * the page reads the car again. Another visit may have closed or opened a line since the draft was
 * started, and a grade the row no longer offers would be refused by record_inspection
 * (INSPECTION_NOTHING_TO_CLOSE, INSPECTION_CLOSURE_REQUIRED) while the row shows nothing chosen.
 *
 *   - a grade the line's row no longer offers is cleared, so the row reads unanswered: Fixed or No
 *     fix needed on a line not open, OK on an open one, and a "Fixed during inspection" Must fix on
 *     a line now open (that switch is for new faults only, and would send Fixed unseen);
 *   - an open item the draft has no line for gets one as a new draft would start it;
 *   - a line added to a re-check that is now open drops out of the added lines (it is pinned).
 *
 * Notes and photos are kept. Returns the same object when nothing changes.
 */
export function reconcileDraft(draft: InspectionDraft, openItems: readonly OpenItem[]): InspectionDraft {
    const pinned = new Set(openItems.map((o) => o.item));
    let lines: Record<string, DraftLine> | null = null;
    for (const [item, line] of Object.entries(draft.lines)) {
        if (!line.grade) continue;
        const isPinned = pinned.has(item);
        const fits = isPinned ? PINNED_GRADES.has(line.grade) && !line.fixedOnSpot : NEW_GRADES.has(line.grade);
        if (fits) continue;
        lines ??= { ...draft.lines };
        lines[item] = { ...line, grade: null, fixedOnSpot: false };
    }
    for (const o of openItems) {
        if (draft.lines[o.item]) continue;
        lines ??= { ...draft.lines };
        lines[o.item] = pinnedLine(draft.kind, o);
    }
    const extraItems = draft.extraItems.some((i) => pinned.has(i)) ? draft.extraItems.filter((i) => !pinned.has(i)) : draft.extraItems;
    if (!lines && extraItems === draft.extraItems) return draft;
    return { ...draft, lines: lines ?? draft.lines, extraItems };
}

/**
 * The change a tap on a grade makes to a line. "Fixed during inspection" survives only on a Must
 * fix. On a pinned line, No fix needed clears the carried fault note it was pre-filled with, so the
 * required note is the reason no fix is needed and not the old fault; leaving No fix needed with
 * the note still empty puts the carried note back.
 */
export function chooseGrade(line: DraftLine, grade: Grade, open: Pick<OpenItem, 'note'> | null | undefined): Partial<DraftLine> {
    const patch: Partial<DraftLine> = { grade, fixedOnSpot: grade === 'fix_now' ? line.fixedOnSpot : false };
    if (!open) return patch;
    const carriedNote = open.note ?? '';
    if (grade === 'not_needed' && line.note.trim() === carriedNote.trim()) patch.note = '';
    else if (line.grade === 'not_needed' && grade !== 'not_needed' && !line.note.trim()) patch.note = carriedNote;
    return patch;
}

/**
 * "All OK" for one section (PLAN §6 step 6): every unanswered line in it that is not a pinned open
 * item becomes OK. Pinned items keep their own answer, and an answered line is never overwritten.
 */
export function applyAllOk(draft: InspectionDraft, sectionItems: readonly string[], pinned: ReadonlySet<string>): InspectionDraft {
    let next = draft;
    for (const item of sectionItems) {
        if (pinned.has(item)) continue;
        if (next.lines[item]?.grade) continue;
        next = setLine(next, item, { grade: 'ok' });
    }
    return next;
}

/** A typed kilometre reading: digits, with spaces or commas allowed; null when empty or not a reading. */
export function parseKm(raw: string): number | null {
    const digits = raw.replace(/[\s,]/g, '');
    if (!/^\d+$/.test(digits)) return null;
    const n = Number(digits);
    return n <= 2_147_483_647 ? n : null;
}
